import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';
import type { EFormAccessSettings, EFormRole, EFormSchema } from './schema';

export type EFormVersionStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type EFormSubmissionStatus = 'DRAFT' | 'SUBMITTED';

export interface EFormVersion {
  id: number;
  definitionId: number;
  version: number;
  status: EFormVersionStatus;
  schemaJson: EFormSchema;
  printLayoutJson?: EFormPrintLayoutJson | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EFormDefinition {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  vesselId?: number | null;
  accessConfig?: EFormAccessSettings | null;
  sourceFileName?: string | null;
  sourceFileMime?: string | null;
  sourceFileSize?: number | null;
  createdAt: string;
  updatedAt: string;
  versions?: EFormVersion[];
  _count?: { submissions: number };
}

export type EFormAccessContext = {
  role: EFormRole | null;
  restricted: boolean;
  canManage: boolean;
  canFill: boolean;
  canView: boolean;
};

export type EFormPrintBlock = {
  id: string;
  kind: 'title' | 'text' | 'field' | 'divider' | 'spacer';
  text?: string;
  fieldKey?: string;
  label?: string;
};

export type EFormPrintOutputMode = 'table' | 'designed' | 'source' | 'none';
export type EFormPrintPageSize =
  | 'A3'
  | 'A4'
  | 'A5'
  | 'LETTER'
  | 'LEGAL'
  | 'EXECUTIVE'
  | 'TABLOID'
  | 'B4'
  | 'B5';

export type EFormPrintMargins = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type EFormPrintFooterContent = 'none' | 'text' | 'datetime' | 'page';

export type EFormPrintFooterSlots = {
  left?: EFormPrintFooterContent;
  center?: EFormPrintFooterContent;
  right?: EFormPrintFooterContent;
};

export type EFormPrintLayout = {
  outputMode?: EFormPrintOutputMode;
  pageSize: EFormPrintPageSize;
  orientation: 'portrait' | 'landscape';
  margins?: EFormPrintMargins;
  title?: string;
  header?: string;
  footer?: string;
  footerSlots?: EFormPrintFooterSlots;
  blocks: EFormPrintBlock[];
};

export type EFormPrintTemplate = EFormPrintLayout & {
  id: string;
  name: string;
};

export type EFormPrintLayoutDocument = {
  schemaVersion: 1;
  defaultTemplateId: string;
  templates: EFormPrintTemplate[];
};

/** Stored JSON may be a legacy single layout or a multi-template document. */
export type EFormPrintLayoutJson = EFormPrintLayout | EFormPrintLayoutDocument;

export type EFormRelation = {
  id: number;
  sourceDefinitionId: number;
  targetDefinitionId: number;
  name: string;
  sourceFieldId: string;
  targetFieldId: string;
  isActive: boolean;
  targetDefinition?: { id: number; code: string; name: string };
};

export type EFormReportSchedule = {
  id: number;
  reportId: number;
  name: string;
  cron: string;
  timezone: string;
  recipients: string[];
  format: 'EXCEL' | 'PDF';
  isEnabled: boolean;
  lastRunAt?: string | null;
  lastError?: string | null;
  lastResultJson?: unknown;
  lastFileStorageKey?: string | null;
  lastFileName?: string | null;
  lastFileMime?: string | null;
};

export type EFormAppPageWidget = {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  settings?: Record<string, unknown>;
};

export type EFormAppPage = {
  id: number;
  definitionId?: number | null;
  name: string;
  description?: string | null;
  layoutJson: { widgets?: EFormAppPageWidget[] };
  sidebarPlacements?: unknown;
  isShared: boolean;
  sortOrder: number;
  definition?: { id: number; code: string; name: string } | null;
};

export interface EFormSubmissionValue {
  id: number;
  submissionId: number;
  fieldId?: string | null;
  fieldKey: string;
  textValue?: string | null;
  numberValue?: number | null;
  dateValue?: string | null;
  boolValue?: boolean | null;
  jsonValue?: unknown;
}

export interface EFormSubmissionApproval {
  id: number;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | string;
  title: string;
  submittedAt?: string | null;
  statusAt?: string | null;
  currentStepLabel?: string | null;
  /** Return / reject / overdue / cancel hint for list & detail icons. */
  notice?: {
    kind?: 'returned' | 'rejected' | 'cancelled' | 'overdue';
    tone: 'warning' | 'danger' | 'info';
    summary: string;
    notes?: string | null;
    byUserName?: string | null;
    at?: string | null;
    stepLabel?: string | null;
  } | null;
}

export interface EFormSubmission {
  id: number;
  definitionId: number;
  versionId: number;
  schemaSnapshot: EFormSchema;
  valuesJson: Record<string, unknown>;
  status: EFormSubmissionStatus;
  submittedAt?: string | null;
  vesselId?: number | null;
  createdAt: string;
  updatedAt: string;
  /** Latest linked approval request, when the form requires approval. */
  approval?: EFormSubmissionApproval | null;
  /** True when latest approval status is APPROVED (locked). */
  locked?: boolean;
  definition?: {
    id: number;
    code: string;
    name: string;
    sourceFileName?: string | null;
    sourceFileMime?: string | null;
    sourceFileSize?: number | null;
  };
  version?: { id: number; version: number; status: string; printLayoutJson?: EFormPrintLayoutJson | null };
  createdByUser?: {
    id: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string | null;
    userEmail?: string;
  } | null;
  values?: EFormSubmissionValue[];
}

export interface PublishedSchemaResponse {
  definition: { id: number; code: string; name: string; description?: string | null };
  version: { id: number; version: number; publishedAt?: string | null };
  schemaJson: EFormSchema;
}

const DEFINITIONS = '/e-form-definitions';
const SUBMISSIONS = '/e-form-submissions';

export function useEFormDefinitions(
  search?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const query = buildListQuery(search, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<EFormDefinition> | EFormDefinition[]
  >(`${DEFINITIONS}${query}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse(data, page, limit);
  return { ...normalized, isLoading, isError: error, mutate };
}

export function useEFormDefinition(id: number) {
  const { data, error, isLoading, mutate } = useSWR<EFormDefinition>(
    id ? `${DEFINITIONS}/${id}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
}

export function useEFormAccessContext(definitionId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormAccessContext>(
    definitionId ? `${DEFINITIONS}/${definitionId}/access-context` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, isLoading, isError: error, mutate };
}

export function useEFormRelations(definitionId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormRelation[]>(
    definitionId ? `${DEFINITIONS}/${definitionId}/relations` : null,
    fetcher,
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
}

export function useEFormPrintLayout(definitionId: number | null, versionId?: number) {
  const suffix = versionId ? `?versionId=${versionId}` : '';
  const { data, error, isLoading, mutate } = useSWR<{
    id: number;
    version: number;
    printLayoutJson?: EFormPrintLayoutJson | null;
  }>(definitionId ? `${DEFINITIONS}/${definitionId}/print-layout${suffix}` : null, fetcher);
  return { data, isLoading, isError: error, mutate };
}

export function useEFormReportSchedules(reportId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormReportSchedule[]>(
    reportId ? `/e-form-reports/${reportId}/schedules` : null,
    fetcher,
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
}

export function useEFormAppPages(definitionId?: number | null, enabled = true) {
  const query = definitionId ? `?definitionId=${definitionId}` : '';
  const { data, error, isLoading, mutate } = useSWR<EFormAppPage[]>(
    enabled ? `/e-form-app-pages${query}` : null,
    fetcher,
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
}

export function useEFormAppPage(pageId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormAppPage>(
    pageId ? `/e-form-app-pages/${pageId}` : null,
    fetcher,
  );
  return { data, isLoading, isError: error, mutate };
}

export function usePublishedEForms(
  search?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
  enabled = true,
) {
  const query = buildListQuery(search, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<EFormDefinition> | EFormDefinition[]
  >(enabled ? `${DEFINITIONS}/published${query}` : null, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse(data, page, limit);
  return { ...normalized, isLoading, isError: error, mutate };
}

export function usePublishedSchema(definitionId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<PublishedSchemaResponse>(
    definitionId ? `${DEFINITIONS}/${definitionId}/published-schema` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, isLoading, isError: error, mutate };
}

export function useEFormSubmissions(
  definitionId: number | null,
  search?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const query = buildListQuery(search, page, limit, options);
  const sep = query.includes('?') ? '&' : '?';
  const url =
    definitionId != null
      ? `${SUBMISSIONS}${query}${sep}definitionId=${definitionId}`
      : null;
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<EFormSubmission> | EFormSubmission[]
  >(url, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse(data, page, limit);
  return { ...normalized, isLoading, isError: error, mutate };
}

export function useEFormSubmission(id: number) {
  const { data, error, isLoading, mutate } = useSWR<EFormSubmission>(
    id ? `${SUBMISSIONS}/${id}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
}

export function submitterDisplayName(s: EFormSubmission): string {
  const u = s.createdByUser;
  if (!u) return '—';
  if (u.userDisplayName) return u.userDisplayName;
  const name = [u.userFirstName, u.userLastName].filter(Boolean).join(' ');
  return name || u.userEmail || '—';
}

export function getDraftVersion(def?: EFormDefinition | null): EFormVersion | undefined {
  return def?.versions?.find((v) => v.status === 'DRAFT');
}

export function getPublishedVersion(def?: EFormDefinition | null): EFormVersion | undefined {
  return def?.versions?.find((v) => v.status === 'PUBLISHED');
}

/** Delete only when never published and no submission records exist. */
export function canDeleteEFormDefinition(def?: EFormDefinition | null): boolean {
  if (!def) return false;
  if (getPublishedVersion(def)) return false;
  return (def._count?.submissions ?? 0) === 0;
}

// --- Dynamic reports ---

export type EFormReportChartType = 'table' | 'bar' | 'line' | 'pie';

export type EFormReportDimension = {
  fieldId?: string;
  fieldKey?: string;
  relationId?: number;
  sourceDefinitionId?: number;
  metaKey?: string;
  bucket?: 'day' | 'week' | 'month' | 'year';
  expandChecklist?: boolean;
};

export type EFormReportMeasure = {
  fn: 'count' | 'count_distinct' | 'sum' | 'avg' | 'min' | 'max' | 'pct_true';
  fieldId?: string;
  fieldKey?: string;
  relationId?: number;
  sourceDefinitionId?: number;
  metaKey?: string;
  alias?: string;
};

export type EFormReportFilter = {
  fieldId?: string;
  fieldKey?: string;
  relationId?: number;
  sourceDefinitionId?: number;
  metaKey?: string;
  op: string;
  value?: unknown;
  valueTo?: unknown;
};

export type EFormReportQuery = {
  scope?: 'current_schema' | 'as_submitted' | 'version_pin';
  versionIds?: number[];
  statuses?: Array<'DRAFT' | 'SUBMITTED'>;
  approvedOnly?: boolean;
  vesselIds?: number[];
  submittedFrom?: string;
  submittedTo?: string;
  filters?: EFormReportFilter[];
  dimensions?: EFormReportDimension[];
  columnDimensions?: EFormReportDimension[];
  measures?: EFormReportMeasure[];
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
  chartType?: EFormReportChartType;
  showTotals?: boolean;
};

export type ReportableField = {
  fieldId: string;
  fieldKey: string;
  historicalKeys: string[];
  label: string;
  definitionId?: number;
  definitionCode?: string;
  definitionName?: string;
  relation?: {
    id: number;
    name: string;
    sourceDefinitionId: number;
    targetDefinitionId: number;
    sourceFieldId: string;
    targetFieldId: string;
    sourceFieldKeys: string[];
    targetFieldKeys: string[];
  };
  dataType: string;
  type: string;
  referenceKey?: string;
  options?: Array<{ value: string; label: string }>;
  role: 'dimension' | 'measure' | 'both' | 'none';
  sectionHeading?: string;
  nestedFields?: ReportableField[];
};

export type EFormReportCatalog = {
  definitionId: number;
  code: string;
  name: string;
  fields: ReportableField[];
  meta: Array<{
    metaKey: string;
    label: string;
    dataType: string;
    role: string;
    bucketable?: boolean;
  }>;
  versions: Array<{ id: number; version: number; status: string; publishedAt: string | null }>;
};

export type EFormReportQueryResult = {
  columns: Array<{
    key: string;
    label: string;
    kind: 'dimension' | 'measure';
    axis?: 'row' | 'column';
    fn?: EFormReportMeasure['fn'];
  }>;
  rows: Array<Record<string, unknown>>;
  totalGroups: number;
  query: EFormReportQuery;
};

export type EFormSavedReport = {
  id: number;
  definitionId: number;
  name: string;
  description?: string | null;
  queryJson: EFormReportQuery;
  chartType: string;
  sortOrder: number;
  isShared: boolean;
  sidebarPlacements?: unknown;
  createdAt: string;
  updatedAt: string;
};

export type EFormSidebarReport = {
  id: number;
  definitionId: number;
  name: string;
  sortOrder: number;
  sidebarPlacements?: unknown;
  formName?: string;
};

export function useEFormReportCatalog(definitionId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormReportCatalog>(
    definitionId ? `${DEFINITIONS}/${definitionId}/reportable-fields` : null,
    fetcher,
    { revalidateOnFocus: false },
  );
  return { data, isLoading, isError: error, mutate };
}

export function useEFormSavedReports(definitionId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<EFormSavedReport[]>(
    definitionId ? `${DEFINITIONS}/${definitionId}/reports/saved` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
}

/** Shared reports with sidebar placements (office menu injection). */
export function useEFormSidebarReports(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR<EFormSidebarReport[]>(
    enabled ? '/e-form-reports/sidebar' : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
}
